import React, { useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Radio,
  TextField,
  Typography,
} from '@mui/material';

import * as approvalV2Api from '../../lib/api/approvalV2';

// 이슈 #7(2026-10-21)로 공개 범위 공유가 켜지면서, 다른 사용자(부서/전체 공개) 템플릿도
// 이 라디오 목록에 섞여 뜬다. 원래 코드(raw LINE_SCOPE)를 그대로 보였는데 한글 라벨로 바꾼다.
const LINE_SCOPE_LABELS = { PRIVATE: '개인', DEPARTMENT: '부서', COMPANY: '전체', SYSTEM: '시스템' };

/**
 * 수주품의서(ORDER_BOOK) 결재선 선택 - 결재 V2 이관(2026-10-20).
 * bito 기존 V1 화면(결재선 1개 레코드에 USR_UUID_1~5를 담는 "결재선" 라디오 선택,
 * OrderBookApprovalForm.js)을 대체한다 - 이제는 결재선 템플릿(여러 단계, 환경설정 > 결재
 * 설정에서 관리)을 고른다. 전결 권한이 있는 사용자는 결재선 없이 바로 승인 처리할 수 있는
 * 체크박스도 함께 보여준다(ApprovalV2Controller의 POST .../documents/final-approve).
 *
 * props:
 *  - lineId, setLineId
 *  - finalApprovalYn, setFinalApprovalYn
 *  - finalApprovalComment, setFinalApprovalComment
 *  - amount: 전결 금액 판정에 쓸 금액(수주 총액)
 */
const OrderBookApprovalFormV2 = ({
  lineId,
  setLineId,
  finalApprovalYn,
  setFinalApprovalYn,
  finalApprovalComment,
  setFinalApprovalComment,
  amount,
}) => {
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [finalAuth, setFinalAuth] = useState(null);

  useEffect(() => {
    approvalV2Api
      .getApprovalV2Lines({ docType: 'ORDER_BOOK' })
      .then((res) => setLines(res?.data?.list || []))
      .finally(() => setLoading(false));

    approvalV2Api
      .getMyFinalApprovalAuth({ docType: 'ORDER_BOOK', amount })
      .then((res) => setFinalAuth(res?.data))
      .catch(() => setFinalAuth(null));
  }, [amount]);

  const handleFinalApprovalToggle = (event) => {
    const checked = event.target.checked;
    setFinalApprovalYn(checked ? 'Y' : 'N');
    if (checked) {
      setLineId(null);
    }
  };

  if (loading) {
    return <Typography variant="body2">로딩중...</Typography>;
  }

  return (
    <Box sx={{ my: 1, px: 2 }}>
      {finalAuth?.canApprove && (
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox checked={finalApprovalYn === 'Y'} onChange={handleFinalApprovalToggle} />
            }
            label="전결로 즉시 승인 처리 (결재선 없이 바로 승인)"
          />
          {finalApprovalYn === 'Y' && (
            <TextField
              fullWidth
              size="small"
              sx={{ mt: 1 }}
              label="전결 처리 의견(선택)"
              value={finalApprovalComment || ''}
              onChange={(e) => setFinalApprovalComment(e.target.value)}
            />
          )}
          <Divider sx={{ mt: 2 }} />
        </Box>
      )}

      {finalApprovalYn !== 'Y' && (
        <>
          <Typography sx={{ mt: 1, mb: 1 }} variant="subtitle1" component="div">
            결재선 템플릿 선택
          </Typography>
          {lines.length === 0 && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              사용할 수 있는 결재선 템플릿이 없습니다. 환경설정 &gt; 결재 설정에서 먼저
              만들어 주세요.
            </Alert>
          )}
          {lines.map((line) => (
            <Box key={line.lineId}>
              <Box sx={{ display: 'flex', alignItems: 'center', my: 1 }}>
                <Radio
                  checked={lineId === line.lineId}
                  onChange={() => setLineId(line.lineId)}
                  value={line.lineId}
                />
                <Typography variant="subtitle2">{line.lineNm}</Typography>
                <Chip
                  size="small"
                  label={LINE_SCOPE_LABELS[line.lineScope] || line.lineScope}
                  variant="outlined"
                  sx={{ ml: 1 }}
                />
              </Box>
              <List
                component="div"
                dense
                sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', p: 0 }}
              >
                {(line.steps || []).map((step) => (
                  <ListItem key={step.lineStepId} sx={{ width: 'auto' }}>
                    <ListItemAvatar>
                      <Avatar>{step.assigneeUsrNm?.charAt(0)}</Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={step.assigneeUsrNm}
                      secondary={`결재자${step.stepOrder}`}
                    />
                  </ListItem>
                ))}
              </List>
              <Divider />
            </Box>
          ))}
        </>
      )}
    </Box>
  );
};

export default OrderBookApprovalFormV2;
